import React, { useContext, useEffect, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useForm, FormProvider } from "react-hook-form";

import api from "apiSingleton";
import { AlertContext, SearchContext } from "context/providers";
import Modal from "components/Modals";
import { getUserDataForEdit, getUserName } from "utils/helpers/users";
import Input from "components/base/Input";
import { ScrollArea } from "components/ui/scroll-area";
import SearchSkillsSelector from "components/SearchSkillsSelector";
import StepIndicators from "components/ui/step-indicators";
import SearchLocations from "./SearchLocations";
import { EXPERIENCE_LEVELS, TK_SKILL_CATEGORY_MAP } from "lookup";
import Button from "components/Button";
import { getGroupedSkillsByCategory } from "utils/common";

const steps = [
  { index: 0, label: "Personal Information" },
  { index: 1, label: "Experience Skills" },
];

const skillSources = [
  {
    key: "careers",
    label: "Careers",
    getTitle: (career) =>
      [career.title, career.companyName].filter(Boolean).join(" at ") ||
      "Untitled career",
  },
  {
    key: "certifications",
    label: "Certifications",
    getTitle: (certification) =>
      certification.name || certification.title || "Untitled certification",
  },
  {
    key: "projectsCaseStudies",
    label: "Projects",
    getTitle: (project) => project.title || "Untitled project",
  },
];

const useFormState = (initialState) => {
  const [formData, setFormData] = useState(initialState);

  const handleInputChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  return { formData, handleInputChange };
};

const ModalFooter = ({
  currentStep,
  onPrevious,
  onNext,
  onSave,
  onCancel,
  hasPermission,
  isLoading,
}) => {
  const isLastStep = !!(currentStep === steps.length - 1);

  const buttonConfig = {
    next: {
      action: onNext,
      text: "Next",
      loadingText: "Next",
    },
    save: {
      action: onSave,
      text: "Save",
      loadingText: "Saving...",
    },
  };

  const nextButtonConfig = isLastStep ? buttonConfig.save : buttonConfig.next;

  return (
    <div className="mt-6 flex justify-between">
      <div>
        {currentStep > 0 && (
          <Button
            onClick={onPrevious}
            isReadOnly={isLoading}
            bgColor="bg-gray-100"
            text="text-gray-700"
          >
            Previous
          </Button>
        )}
      </div>
      <div className="flex space-x-3">
        <Button
          onClick={nextButtonConfig.action}
          bgColor="bg-blue-500 hover:bg-blue-100"
          text="text-white"
          isReadOnly={isLoading || !hasPermission}
        >
          {isLoading ? "Saving" : nextButtonConfig.text}
        </Button>
        <Button
          onClick={onCancel}
          disabled={!hasPermission}
          isReadOnly={isLoading}
          bgColor="bg-gray-100"
          text="text-gray-700"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

const PermissionCheckbox = ({ hasPermission, setHasPermission }) => (
  <div
    className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md cursor-pointer"
    onClick={() => setHasPermission(!hasPermission)}
  >
    <div className="flex items-center pointer-events-none">
      <input
        type="checkbox"
        id="permission-checkbox"
        checked={hasPermission}
        onChange={() => setHasPermission(!hasPermission)}
        className="mt-1 mr-2"
      />
      <label htmlFor="permission-checkbox" className="text-yellow-800">
        I certify that I have the explicit permission of the talent to modify
        their profile on their behalf.
      </label>
    </div>
  </div>
);

const UserEditProfileModal = ({ user: hit, onClose }) => {
  const { addGraphQLAlert } = useContext(AlertContext);
  const { setHits, hits } = useContext(SearchContext);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [user, setUser] = useState(null);

  useEffect(() => {
    (async () => {
      const userDataForEdit = await getUserDataForEdit(hit.email);
      setUser(userDataForEdit);
    })();
  }, [hit]);

  const methods = useForm({
    defaultValues: {
      location: {},
    },
  });

  const { formData, handleInputChange } = useFormState({
    given_name: "",
    family_name: "",
    location: {},
    tagline: "",
    bio: "",
    ratePerHour: {
      value: null,
      currency: "USD",
    },
    salary: {
      value: null,
      currency: "USD",
    },
    careers: [],
    certifications: [],
    projectsCaseStudies: [],
  });

  useEffect(() => {
    if (user) {
      const initialFormData = {
        given_name: user.given_name || "",
        family_name: user.family_name || "",
        location: user.location || {},
        tagline: user.tagline || "",
        bio: user.bio || "",
        ratePerHour: {
          value:
            user.ratePerHour?.value !== undefined
              ? user.ratePerHour.value
              : null,
          currency: "USD",
        },
        salary: {
          value: user.salary?.value !== undefined ? user.salary.value : null,
          currency: "USD",
        },
        careers: user.careers || [],
        certifications: user.certifications || [],
        projectsCaseStudies: user.projectsCaseStudies || [],
      };

      Object.entries(initialFormData).forEach(([key, value]) => {
        handleInputChange(key, value);
      });
      methods.setValue("location", initialFormData.location);
    }
  }, [user, methods, handleInputChange]);

  const updateSearchResults = (updatedUser) => {
    if (!updatedUser) return;

    const updatedHits = hits.map((hit) =>
      hit.id === updatedUser.id ? { ...hit, ...updatedUser } : hit
    );
    setHits(updatedHits);
  };

  const handleSave = async () => {
    if (!hasPermission) {
      addGraphQLAlert({
        message:
          "Please certify that you have permission to modify the profile",
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        id: user.id,
        ...formData,
      };

      if (!payload.ratePerHour?.value) {
        payload.ratePerHour.value = 0;
      }

      const { data } = await api.user.update({
        input: payload,
      });
      updateSearchResults(data?.updateUser);
      onClose();
    } catch (err) {
      console.log("Error updating profile:", err);
      addGraphQLAlert(err);
    } finally {
      setIsLoading(false);
    }
  };

  const updateEntitySkills = (sourceKey, entityIndex, updateSkills) => {
    handleInputChange(
      sourceKey,
      formData[sourceKey].map((entity, index) =>
        index === entityIndex
          ? { ...entity, TKSkills: updateSkills(entity.TKSkills || []) }
          : entity
      )
    );
  };

  const renderPersonalStep = () => (
    <>
      <PermissionCheckbox
        hasPermission={hasPermission}
        setHasPermission={setHasPermission}
      />

      <div className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input
            label="Given Name"
            value={formData.given_name}
            disabled={!hasPermission}
            onChange={(e) => handleInputChange("given_name", e.value)}
          />
          <Input
            label="Family Name"
            value={formData.family_name}
            disabled={!hasPermission}
            onChange={(e) => handleInputChange("family_name", e.value)}
          />
        </div>

        <SearchLocations
          location={methods.watch("location")}
          setLocation={(location) => {
            handleInputChange("location", location);
            methods.setValue("location", location);
          }}
          disabled={!hasPermission}
        />

        <Input
          label="Headline"
          value={formData.tagline}
          disabled={!hasPermission}
          onChange={(e) => handleInputChange("tagline", e.value)}
        />
        <ScrollArea>
          <label>Summary</label>
          <textarea
            value={formData.bio}
            onChange={(e) => handleInputChange("bio", e.target.value)}
            disabled={!hasPermission}
            className="w-full border-2 outline-none p-2 rounded transition-all focus:border-gray-300"
          />
        </ScrollArea>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="col-span-3">
            <div className="flex items-center gap-4 mb-2 mt-2">
              <label className="block text-md font-medium">
                Expected compensation in U.S. Dollars (USD)
              </label>
            </div>
            <div className="flex gap-4">
              <div>
                <Input
                  label="Rate per Hour (in $)"
                  value={formData.ratePerHour.value}
                  disabled={!hasPermission}
                  onChange={(e) => {
                    handleInputChange("ratePerHour", {
                      ...formData.ratePerHour,
                      value: e.value ? parseFloat(e.value) : null,
                    });
                  }}
                  type="number"
                  className="w-48 mb-0"
                />
                {!!formData.ratePerHour.value && (
                  <span className="text-sm">
                    Monthly Equivalent $
                    {(formData.ratePerHour.value * 173).toFixed(0)}
                  </span>
                )}
              </div>
              <div>
                <Input
                  label="Yearly Salary (in $)"
                  value={formData.salary.value}
                  disabled={!hasPermission}
                  onChange={(e) => {
                    handleInputChange("salary", {
                      ...formData.ratePerHour,
                      value: e.value ? parseFloat(e.value) : null,
                    });
                  }}
                  type="number"
                  className="w-48 mb-0"
                />
                {!!formData.salary.value && (
                  <span className="text-sm">
                    Monthly Equivalent $
                    {(formData.salary.value / 12).toFixed(0)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderEntitySkills = (source, entity, entityIndex) => {
    const skills = entity.TKSkills || [];
    const skillsByCategory = getGroupedSkillsByCategory(skills);
    const updateSkills = (updater) =>
      updateEntitySkills(source.key, entityIndex, updater);

    return (
      <div key={entity.id || entityIndex} className="border rounded-md p-3">
        <h4 className="font-semibold text-gray-900 mb-2">
          {source.getTitle(entity)}
        </h4>
        <SearchSkillsSelector
          selectSkillCallback={(skill) =>
            updateSkills((currentSkills) =>
              currentSkills.some((currentSkill) => currentSkill.id === skill.id)
                ? currentSkills
                : [
                    ...currentSkills,
                    {
                      id: skill.id,
                      category: skill.category,
                      name: skill.name,
                      experience: "low",
                    },
                  ]
            )
          }
          hasPermission={hasPermission}
          selectedSkills={skills}
        />
        {skills.length > 0 && (
          <div className="mt-3 space-y-3">
            {Object.keys(skillsByCategory).map((category) => (
              <div key={category}>
                <p className="uppercase text-sm text-gray-600 font-semibold">
                  {TK_SKILL_CATEGORY_MAP[category] || category}
                </p>
                {skillsByCategory[category].map((skill) => (
                  <div
                    key={skill.id}
                    className="flex flex-wrap justify-between items-center gap-2 py-2 border-b last:border-b-0"
                  >
                    <span className="font-medium text-gray-900">
                      {skill.name}
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      {EXPERIENCE_LEVELS.map((level) => (
                        <button
                          type="button"
                          key={level.value}
                          onClick={() =>
                            updateSkills((currentSkills) =>
                              currentSkills.map((currentSkill) =>
                                currentSkill.id === skill.id
                                  ? {
                                      ...currentSkill,
                                      experience: level.value,
                                    }
                                  : currentSkill
                              )
                            )
                          }
                          className={`px-3 py-1 rounded-md text-sm ${
                            skill.experience === level.value
                              ? "bg-blue-500 text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                          disabled={!hasPermission}
                        >
                          {level.label}
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() =>
                          updateSkills((currentSkills) =>
                            currentSkills.filter(
                              (currentSkill) => currentSkill.id !== skill.id
                            )
                          )
                        }
                        className="text-red-600 hover:text-red-800 ml-2"
                        disabled={!hasPermission}
                        aria-label={`Remove ${skill.name}`}
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const renderSkillsStep = () => (
    <ScrollArea className="h-[55vh] pr-3">
      <p className="mb-4 text-sm text-gray-600">
        Skills are attached to the experience where the talent used or earned
        them.
      </p>
      <div className="space-y-5">
        {skillSources.map((source) => (
          <section key={source.key}>
            <h3 className="text-lg font-semibold mb-2">{source.label}</h3>
            <div className="space-y-3">
              {formData[source.key].length > 0 ? (
                formData[source.key].map((entity, entityIndex) =>
                  renderEntitySkills(source, entity, entityIndex)
                )
              ) : (
                <p className="text-sm text-gray-500">
                  No {source.label.toLowerCase()} on this profile.
                </p>
              )}
            </div>
          </section>
        ))}
      </div>
    </ScrollArea>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 0:
        return renderPersonalStep();
      case 1:
        return renderSkillsStep();
      default:
        return null;
    }
  };

  const verifyNextButtonPermission = () => {
    if (!hasPermission) return false;

    switch (currentStep) {
      case 0: {
        const { given_name, family_name, ratePerHour, location } = formData;
        return !!(
          given_name?.trim() &&
          family_name?.trim() &&
          ratePerHour?.value >= 0 &&
          location
        );
      }
      case 1: {
        return true;
      }
      default:
        return false;
    }
  };

  return (
    <Modal
      isVisible={user}
      title="Edit Profile"
      subTitle={`User: ${getUserName(user)}`}
      onClose={onClose}
      className="max-h-[90vh] !overflow-y-auto border-2 overflow-hidden xl:!w-7/12 md:!w-10/12 !w-[95%]"
    >
      <div className="flex flex-col h-full">
        <div className="p-3 flex-shrink-0">
          <StepIndicators steps={steps} currentStep={currentStep} />
        </div>
        <div className="flex-1 overflow-y-auto px-3">
          <FormProvider {...methods}>{renderCurrentStep()}</FormProvider>
        </div>
        <div className="p-3 flex-shrink-0">
          <ModalFooter
            currentStep={currentStep}
            onPrevious={() => setCurrentStep(currentStep - 1)}
            onNext={() => setCurrentStep(currentStep + 1)}
            onSave={handleSave}
            onCancel={onClose}
            hasPermission={verifyNextButtonPermission()}
            isLoading={isLoading}
          />
        </div>
      </div>
    </Modal>
  );
};

UserEditProfileModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  user: PropTypes.shape({
    id: PropTypes.string.isRequired,
    given_name: PropTypes.string,
    family_name: PropTypes.string,
    location: PropTypes.object,
    bio: PropTypes.string,
    ratePerHour: PropTypes.shape({
      value: PropTypes.number,
    }),
  }),
};

UserEditProfileModal.defaultProps = {
  user: null,
};

export default UserEditProfileModal;
